"use client";

import { useEffect, useMemo, useState } from "react";
import {
  AlertCircle,
  Clock3,
  Link2,
  Network,
  Sparkles,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { ClearButton } from "@/components/tools/clear-button";
import { CopyButton } from "@/components/tools/copy-button";
import { ErrorMessage } from "@/components/tools/error-message";
import { InputOutputPanel } from "@/components/tools/input-output-panel";
import { CodeOutput } from "@/components/tools/tool-output";
import {
  formatRedirectChainReport,
  isRedirectStatus,
  redirectStatusType,
  statusTone,
  type RedirectChainAnalysis,
  type RedirectChainFindingStatus,
  type RedirectHop,
} from "@/lib/tools/redirect-chain";
import { cn } from "@/lib/utils";

const REDIRECT_HISTORY_KEY = "localtools:redirect-tracker-history";
const MAX_BATCH_URLS = 12;

type RedirectRequestProfile = "standard" | "browser" | "crawler";
type RedirectInputMode = "single" | "batch";
type RedirectView = "overview" | "timeline" | "insights" | "headers";

type RedirectChainResponse = {
  requestedUrl: string;
  finalUrl: string;
  method: "HEAD" | "GET";
  methodFallback: boolean;
  profile: RedirectRequestProfile;
  timeoutMs: number;
  maxRedirects: number;
  terminalReason:
    | "complete"
    | "redirect-loop"
    | "too-many-redirects"
    | "blocked-redirect"
    | "redirect-without-location";
  totalDurationMs: number;
  redirectCount: number;
  warning?: string;
  hops: RedirectHop[];
  analysis: RedirectChainAnalysis;
};

type RedirectBatchItem = {
  id: string;
  url: string;
  status: "pending" | "complete" | "error";
  result?: RedirectChainResponse;
  error?: string;
};

type RedirectHistoryItem = {
  id: string;
  requestedUrl: string;
  finalUrl: string;
  grade: string;
  score: number;
  redirectCount: number;
  totalDurationMs: number;
  terminalReason: RedirectChainResponse["terminalReason"];
  checkedAt: string;
};

const statusToneClasses: Record<ReturnType<typeof statusTone>, string> = {
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  redirect: "border-sky-200 bg-sky-50 text-sky-800",
  "client-error": "border-amber-200 bg-amber-50 text-amber-800",
  "server-error": "border-red-200 bg-red-50 text-red-800",
  other: "border-zinc-200 bg-zinc-50 text-zinc-700",
};

const findingStatusClasses: Record<RedirectChainFindingStatus, string> = {
  pass: "border-emerald-200 bg-emerald-50 text-emerald-800",
  warning: "border-amber-200 bg-amber-50 text-amber-800",
  fail: "border-red-200 bg-red-50 text-red-800",
  info: "border-sky-200 bg-sky-50 text-sky-800",
};

function readRedirectHistory() {
  if (typeof window === "undefined") {
    return [];
  }

  try {
    const parsed: unknown = JSON.parse(window.localStorage.getItem(REDIRECT_HISTORY_KEY) ?? "[]");

    return Array.isArray(parsed)
      ? parsed.filter((item): item is RedirectHistoryItem => {
          return (
            typeof item === "object" &&
            item !== null &&
            "requestedUrl" in item &&
            typeof item.requestedUrl === "string"
          );
        })
      : [];
  } catch {
    return [];
  }
}

function writeRedirectHistory(history: RedirectHistoryItem[]) {
  if (typeof window === "undefined") {
    return;
  }

  window.localStorage.setItem(REDIRECT_HISTORY_KEY, JSON.stringify(history));
}

function historyItemFromResult(result: RedirectChainResponse): RedirectHistoryItem {
  return {
    id: `${Date.now()}-${result.requestedUrl}`,
    requestedUrl: result.requestedUrl,
    finalUrl: result.analysis.finalUrl,
    grade: result.analysis.grade,
    score: result.analysis.score,
    redirectCount: result.analysis.redirectCount,
    totalDurationMs: result.analysis.totalDurationMs,
    terminalReason: result.terminalReason,
    checkedAt: new Date().toISOString(),
  };
}

function mergeHistory(current: RedirectHistoryItem[], results: RedirectChainResponse[]) {
  const incoming = results.map(historyItemFromResult);
  const next = [...incoming, ...current].filter(
    (item, index, list) =>
      list.findIndex((candidate) => candidate.requestedUrl === item.requestedUrl) === index,
  );

  return next.slice(0, 10);
}

function stringifyRedirectHeaders(hop: RedirectHop) {
  return hop.headers.map((header) => `${header.name}: ${header.value}`).join("\n");
}

function stringifyRedirectResult(result: RedirectChainResponse | null) {
  if (!result) {
    return "";
  }

  return formatRedirectChainReport({
    requestedUrl: result.requestedUrl,
    method: result.method,
    analysis: result.analysis,
    hops: result.hops,
  });
}

function csvCell(value: string | number | boolean | undefined) {
  const text = String(value ?? "");

  if (/[",\n]/.test(text)) {
    return `"${text.replace(/"/g, '""')}"`;
  }

  return text;
}

function stringifyRedirectCsv(results: RedirectChainResponse[]) {
  const rows = [
    [
      "requested_url",
      "final_url",
      "grade",
      "score",
      "terminal_reason",
      "hop",
      "method",
      "status",
      "duration_ms",
      "hop_url",
      "location",
      "protocol_change",
      "hostname_changed",
      "path_changed",
      "query_changed",
    ],
    ...results.flatMap((result) =>
      result.hops.map((hop) => {
        const diagnostic = result.analysis.diagnostics.hopDiagnostics.find(
          (item) => item.hopIndex === hop.index,
        );

        return [
          result.requestedUrl,
          result.analysis.finalUrl,
          result.analysis.grade,
          result.analysis.score,
          result.terminalReason,
          hop.index,
          hop.method,
          `${hop.status} ${hop.statusText}`,
          hop.durationMs,
          hop.url,
          hop.resolvedLocation ?? "",
          diagnostic?.protocolChange ?? "",
          diagnostic?.hostnameChanged ?? "",
          diagnostic?.pathChanged ?? "",
          diagnostic?.queryChanged ?? "",
        ];
      }),
    ),
  ];

  return rows.map((row) => row.map(csvCell).join(",")).join("\n");
}

function parseBatchUrls(value: string) {
  return [
    ...new Set(
      value
        .split(/\r?\n/)
        .map((line) => line.trim())
        .filter(Boolean),
    ),
  ];
}

function scoreTone(score: number) {
  if (score >= 88) {
    return "text-emerald-700";
  }

  if (score >= 65) {
    return "text-amber-700";
  }

  return "text-red-700";
}

function statusLabel(hop: RedirectHop) {
  const type = redirectStatusType(hop.status);

  if (type === "permanent") {
    return "Permanent";
  }

  if (type === "temporary") {
    return "Temporary";
  }

  if (isRedirectStatus(hop.status)) {
    return "Redirect";
  }

  if (hop.status >= 200 && hop.status < 300) {
    return "Final";
  }

  return "Response";
}

function topIssue(result: RedirectChainResponse) {
  return result.analysis.topFixes[0]?.title ?? "No priority fix";
}

function MetricCard({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: string;
}) {
  return (
    <div className="rounded-md border border-zinc-200 bg-white p-3">
      <p className="text-xs font-medium uppercase text-zinc-500">{label}</p>
      <p className={cn("mt-1 break-all font-mono text-lg font-semibold text-zinc-950", tone)}>
        {value}
      </p>
      {detail ? <p className="mt-1 text-xs leading-5 text-zinc-500">{detail}</p> : null}
    </div>
  );
}

function RedirectChainMap({ result }: { result: RedirectChainResponse }) {
  return (
    <div className="overflow-x-auto rounded-md border border-zinc-200 bg-zinc-50 p-3">
      <div className="flex min-w-max items-center gap-2">
        {result.hops.map((hop, index) => (
          <div key={`${hop.index}-${hop.url}`} className="flex items-center gap-2">
            <div className="min-w-32 rounded-md border border-zinc-200 bg-white px-3 py-2">
              <p className="text-xs font-medium text-zinc-500">Hop {hop.index}</p>
              <p className="mt-1 font-mono text-sm font-semibold text-zinc-950">{hop.status}</p>
            </div>
            {index < result.hops.length - 1 ? (
              <span className="text-sm font-medium text-zinc-400">-&gt;</span>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}

function RedirectDiagnostics({ result }: { result: RedirectChainResponse }) {
  const { diagnostics } = result.analysis;
  const queryDelta = diagnostics.queryDelta;
  const headerSignals = diagnostics.headerSignals;

  const items = [
    {
      label: "Protocol",
      value: diagnostics.urlDelta.protocolChanged
        ? `${diagnostics.urlDelta.initialProtocol} -> ${diagnostics.urlDelta.finalProtocol}`
        : diagnostics.urlDelta.finalProtocol || "unknown",
      tone: diagnostics.urlDelta.downgradedToHttp ? "text-red-700" : undefined,
    },
    {
      label: "Host",
      value: diagnostics.urlDelta.hostnameChanged
        ? `${diagnostics.urlDelta.initialHostname} -> ${diagnostics.urlDelta.finalHostname}`
        : diagnostics.urlDelta.finalHostname || "unknown",
    },
    {
      label: "Path",
      value: diagnostics.urlDelta.pathChanged
        ? `${diagnostics.urlDelta.initialPathname} -> ${diagnostics.urlDelta.finalPathname}`
        : diagnostics.urlDelta.finalPathname || "/",
    },
    {
      label: "Query",
      value: queryDelta.preserved ? "preserved" : "changed",
      detail: queryDelta.preserved
        ? queryDelta.initial || "none"
        : [
            queryDelta.removed.length ? `removed ${queryDelta.removed.join(", ")}` : "",
            queryDelta.added.length ? `added ${queryDelta.added.join(", ")}` : "",
            queryDelta.changed.length ? `changed ${queryDelta.changed.join(", ")}` : "",
          ]
            .filter(Boolean)
            .join("; "),
      tone: queryDelta.removed.length || queryDelta.changed.length ? "text-amber-700" : undefined,
    },
    {
      label: "Status mix",
      value: `${diagnostics.statusBreakdown.permanent} perm / ${diagnostics.statusBreakdown.temporary} temp`,
      detail: diagnostics.statusBreakdown.special
        ? `${diagnostics.statusBreakdown.special} special redirect`
        : undefined,
    },
    {
      label: "Headers",
      value: headerSignals.finalContentType ?? "not reported",
      detail: headerSignals.finalCdnCacheStatus
        ? `cache ${headerSignals.finalCdnCacheStatus}`
        : headerSignals.finalCacheControl,
    },
  ];

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      {items.map((item) => (
        <MetricCard
          key={item.label}
          label={item.label}
          value={item.value}
          detail={item.detail}
          tone={item.tone}
        />
      ))}
    </div>
  );
}

function RedirectHopCard({
  result,
  hop,
  isLast,
}: {
  result: RedirectChainResponse;
  hop: RedirectHop;
  isLast: boolean;
}) {
  const diagnostic = result.analysis.diagnostics.hopDiagnostics.find(
    (item) => item.hopIndex === hop.index,
  );
  const badges = diagnostic
    ? [
        diagnostic.protocolChange !== "same" ? diagnostic.protocolChange : "",
        diagnostic.hostnameChanged ? "host" : "",
        diagnostic.pathChanged ? "path" : "",
        diagnostic.queryChanged ? "query" : "",
        diagnostic.relativeLocation ? "relative Location" : "",
        diagnostic.setsCookie ? "sets cookie" : "",
      ].filter(Boolean)
    : [];

  return (
    <div className="rounded-md border border-zinc-200 bg-white p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span className="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
              Hop {hop.index}
            </span>
            <span
              className={cn(
                "rounded-md border px-2 py-1 text-xs font-medium",
                statusToneClasses[statusTone(hop.status)],
              )}
            >
              {hop.status} {hop.statusText || statusLabel(hop)}
            </span>
            <span className="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
              {statusLabel(hop)}
            </span>
            <span className="inline-flex items-center gap-1 rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
              <Clock3 className="h-3.5 w-3.5" />
              {hop.durationMs} ms
            </span>
            {badges.map((badge) => (
              <span
                key={badge}
                className="rounded-md border border-sky-200 bg-sky-50 px-2 py-1 text-xs font-medium text-sky-800"
              >
                {badge}
              </span>
            ))}
          </div>
          <p className="break-all font-mono text-sm text-zinc-950">{hop.url}</p>
        </div>
        {isLast ? (
          <span className="shrink-0 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1 text-xs font-medium text-emerald-800">
            Final URL
          </span>
        ) : null}
      </div>
      {hop.resolvedLocation ? (
        <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2">
          <p className="text-xs font-medium uppercase text-zinc-500">Location</p>
          <p className="mt-1 break-all font-mono text-sm text-zinc-900">{hop.resolvedLocation}</p>
        </div>
      ) : null}
      {diagnostic?.cacheControl || diagnostic?.cdnCacheStatus ? (
        <div className="mt-3 grid gap-2 text-xs sm:grid-cols-2">
          {diagnostic.cacheControl ? (
            <p className="break-all rounded-md bg-zinc-50 px-2 py-1 text-zinc-600">
              cache-control: {diagnostic.cacheControl}
            </p>
          ) : null}
          {diagnostic.cdnCacheStatus ? (
            <p className="break-all rounded-md bg-zinc-50 px-2 py-1 text-zinc-600">
              cache signal: {diagnostic.cdnCacheStatus}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function RedirectFindings({ analysis }: { analysis: RedirectChainAnalysis }) {
  return (
    <div className="space-y-3">
      {analysis.findings.map((finding) => (
        <div key={finding.id} className="rounded-md border border-zinc-200 bg-white p-4">
          <div className="mb-2 flex flex-wrap items-center gap-2">
            <span
              className={cn(
                "rounded-md border px-2 py-1 text-xs font-medium",
                findingStatusClasses[finding.status],
              )}
            >
              {finding.status}
            </span>
            {finding.evidence ? (
              <span className="rounded-md bg-zinc-100 px-2 py-1 text-xs font-medium text-zinc-600">
                evidence
              </span>
            ) : null}
          </div>
          <h3 className="text-sm font-semibold text-zinc-950">{finding.title}</h3>
          <p className="mt-1 text-sm leading-6 text-zinc-600">{finding.summary}</p>
          <div className="mt-3 rounded-md border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm leading-6 text-zinc-700">
            {finding.recommendation}
          </div>
          {finding.evidence ? (
            <CodeOutput value={finding.evidence} className="mt-3 min-h-0 text-xs" />
          ) : null}
        </div>
      ))}
    </div>
  );
}

function RedirectOverview({ result }: { result: RedirectChainResponse }) {
  return (
    <div className="space-y-5">
      <div className="grid gap-3 md:grid-cols-[10rem_1fr]">
        <div className="rounded-md border border-zinc-200 bg-zinc-50 p-4 text-center">
          <p className={cn("text-5xl font-semibold", scoreTone(result.analysis.score))}>
            {result.analysis.grade}
          </p>
          <p className="mt-2 font-mono text-sm text-zinc-600">{result.analysis.score}/100</p>
        </div>
        <div className="rounded-md border border-zinc-200 bg-white p-4">
          <div className="grid gap-2 text-sm sm:grid-cols-2">
            <div>
              <p className="text-zinc-500">Final URL</p>
              <p className="mt-1 break-all font-mono text-zinc-950">{result.analysis.finalUrl}</p>
            </div>
            <div>
              <p className="text-zinc-500">Final status</p>
              <p className="mt-1 font-mono text-zinc-950">{result.analysis.finalStatus}</p>
            </div>
            <div>
              <p className="text-zinc-500">Redirects</p>
              <p className="mt-1 font-mono text-zinc-950">{result.analysis.redirectCount}</p>
            </div>
            <div>
              <p className="text-zinc-500">Total time</p>
              <p className="mt-1 font-mono text-zinc-950">
                {result.analysis.totalDurationMs} ms
              </p>
            </div>
          </div>
          <p className="mt-3 text-sm leading-6 text-zinc-600">{result.analysis.summary}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-xs font-medium">
            <span className="rounded-md bg-zinc-100 px-2 py-1 text-zinc-600">
              {result.profile} profile
            </span>
            <span className="rounded-md bg-zinc-100 px-2 py-1 text-zinc-600">
              {result.method}
            </span>
            <span className="rounded-md bg-zinc-100 px-2 py-1 text-zinc-600">
              {result.timeoutMs / 1000}s timeout
            </span>
          </div>
          {result.methodFallback ? (
            <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              HEAD was not supported, so the tracker retried with a byte-range GET request.
            </p>
          ) : null}
          {result.warning ? (
            <p className="mt-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {result.warning}
            </p>
          ) : null}
        </div>
      </div>

      <RedirectChainMap result={result} />
      <RedirectDiagnostics result={result} />

      {result.analysis.topFixes.length ? (
        <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
          <h3 className="text-sm font-semibold text-amber-900">Priority cleanup</h3>
          <div className="mt-2 space-y-2 text-sm leading-6 text-amber-800">
            {result.analysis.topFixes.map((finding) => (
              <p key={finding.id}>
                <span className="font-medium">{finding.title}:</span> {finding.recommendation}
              </p>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function RedirectResult({
  result,
  view,
}: {
  result: RedirectChainResponse | null;
  view: RedirectView;
}) {
  if (!result) {
    return <CodeOutput placeholder="Redirect traces, batch results, and diagnostics will appear here." />;
  }

  if (view === "headers") {
    return (
      <div className="space-y-4">
        {result.hops.map((hop) => (
          <div key={`${hop.index}-${hop.url}`} className="space-y-2">
            <h3 className="text-sm font-semibold text-zinc-700">
              Hop {hop.index} headers - {hop.status}
            </h3>
            <CodeOutput
              value={stringifyRedirectHeaders(hop)}
              placeholder="No headers were returned for this hop."
              className="min-h-0 text-xs"
            />
          </div>
        ))}
      </div>
    );
  }

  if (view === "insights") {
    return <RedirectFindings analysis={result.analysis} />;
  }

  if (view === "timeline") {
    return (
      <div className="space-y-3">
        {result.hops.map((hop, index) => (
          <RedirectHopCard
            key={`${hop.index}-${hop.url}`}
            result={result}
            hop={hop}
            isLast={index === result.hops.length - 1}
          />
        ))}
      </div>
    );
  }

  return <RedirectOverview result={result} />;
}

function BatchSummary({
  items,
  selectedId,
  onSelect,
}: {
  items: RedirectBatchItem[];
  selectedId: string | null;
  onSelect: (id: string) => void;
}) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="overflow-hidden rounded-md border border-zinc-200">
      <table className="w-full text-left text-sm">
        <thead className="bg-zinc-50 text-zinc-600">
          <tr>
            <th className="px-3 py-2 font-medium">URL</th>
            <th className="px-3 py-2 font-medium">Grade</th>
            <th className="px-3 py-2 font-medium">Redirects</th>
            <th className="px-3 py-2 font-medium">Time</th>
            <th className="px-3 py-2 font-medium">Signal</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item) => (
            <tr key={item.id} className="border-t border-zinc-100">
              <td className="min-w-52 px-3 py-2">
                <button
                  type="button"
                  className={cn(
                    "break-all text-left font-mono text-xs hover:text-zinc-950",
                    selectedId === item.id ? "text-zinc-950" : "text-zinc-600",
                  )}
                  onClick={() => onSelect(item.id)}
                >
                  {item.url}
                </button>
              </td>
              <td className="whitespace-nowrap px-3 py-2 font-mono text-zinc-950">
                {item.status === "complete" ? item.result?.analysis.grade : item.status}
              </td>
              <td className="px-3 py-2 font-mono text-zinc-950">
                {item.result?.analysis.redirectCount ?? "-"}
              </td>
              <td className="px-3 py-2 font-mono text-zinc-950">
                {item.result ? `${item.result.analysis.totalDurationMs} ms` : "-"}
              </td>
              <td className="min-w-48 px-3 py-2 text-zinc-600">
                {item.error ?? (item.result ? topIssue(item.result) : "Waiting")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RedirectTrackerOutput({
  batchItems,
  selectedBatchId,
  onSelectBatch,
  result,
  view,
}: {
  batchItems: RedirectBatchItem[];
  selectedBatchId: string | null;
  onSelectBatch: (id: string) => void;
  result: RedirectChainResponse | null;
  view: RedirectView;
}) {
  if (!batchItems.length) {
    return <RedirectResult result={result} view={view} />;
  }

  const selectedResult =
    batchItems.find((item) => item.id === selectedBatchId)?.result ??
    batchItems.find((item) => item.result)?.result ??
    null;

  return (
    <div className="space-y-5">
      <BatchSummary items={batchItems} selectedId={selectedBatchId} onSelect={onSelectBatch} />
      <RedirectResult result={selectedResult} view={view} />
    </div>
  );
}

function RecentRedirectHistory({
  history,
  onPick,
  onClear,
}: {
  history: RedirectHistoryItem[];
  onPick: (url: string) => void;
  onClear: () => void;
}) {
  if (!history.length) {
    return null;
  }

  return (
    <div className="rounded-md border border-zinc-200 bg-zinc-50 p-3">
      <div className="mb-2 flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-zinc-800">Recent traces</h3>
        <Button type="button" variant="ghost" size="sm" onClick={onClear}>
          Clear
        </Button>
      </div>
      <div className="space-y-2">
        {history.slice(0, 5).map((item) => (
          <button
            key={item.id}
            type="button"
            className="flex w-full items-start justify-between gap-3 rounded-md bg-white px-3 py-2 text-left text-sm hover:bg-zinc-100"
            onClick={() => onPick(item.requestedUrl)}
          >
            <span className="min-w-0">
              <span className="block break-all font-mono text-xs text-zinc-950">
                {item.requestedUrl}
              </span>
              <span className="mt-1 block text-xs text-zinc-500">
                {item.redirectCount} redirects / {item.totalDurationMs} ms
              </span>
            </span>
            <span className={cn("font-mono text-sm font-semibold", scoreTone(item.score))}>
              {item.grade}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

export function RedirectChainCheckerTool() {
  const [inputMode, setInputMode] = useState<RedirectInputMode>("single");
  const [url, setUrl] = useState("");
  const [batchUrls, setBatchUrls] = useState("");
  const [method, setMethod] = useState<"HEAD" | "GET">("HEAD");
  const [profile, setProfile] = useState<RedirectRequestProfile>("standard");
  const [timeoutMs, setTimeoutMs] = useState(10000);
  const [maxRedirects, setMaxRedirects] = useState(10);
  const [result, setResult] = useState<RedirectChainResponse | null>(null);
  const [batchItems, setBatchItems] = useState<RedirectBatchItem[]>([]);
  const [selectedBatchId, setSelectedBatchId] = useState<string | null>(null);
  const [history, setHistory] = useState<RedirectHistoryItem[]>([]);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [view, setView] = useState<RedirectView>("overview");

  useEffect(() => {
    setHistory(readRedirectHistory());
  }, []);

  const completedBatchResults = useMemo(
    () => batchItems.map((item) => item.result).filter((item): item is RedirectChainResponse => Boolean(item)),
    [batchItems],
  );
  const activeResult = useMemo(() => {
    if (!batchItems.length) {
      return result;
    }

    return (
      batchItems.find((item) => item.id === selectedBatchId)?.result ??
      batchItems.find((item) => item.result)?.result ??
      null
    );
  }, [batchItems, result, selectedBatchId]);
  const reportValue = useMemo(() => stringifyRedirectResult(activeResult), [activeResult]);
  const csvValue = useMemo(() => {
    const rows = completedBatchResults.length ? completedBatchResults : activeResult ? [activeResult] : [];

    return rows.length ? stringifyRedirectCsv(rows) : "";
  }, [activeResult, completedBatchResults]);
  const jsonValue = useMemo(() => {
    const payload = completedBatchResults.length ? completedBatchResults : activeResult;

    return payload ? JSON.stringify(payload, null, 2) : "";
  }, [activeResult, completedBatchResults]);

  const rememberResults = (results: RedirectChainResponse[]) => {
    setHistory((current) => {
      const next = mergeHistory(current, results);

      writeRedirectHistory(next);
      return next;
    });
  };

  const clear = () => {
    setUrl("");
    setBatchUrls("");
    setMethod("HEAD");
    setProfile("standard");
    setTimeoutMs(10000);
    setMaxRedirects(10);
    setResult(null);
    setBatchItems([]);
    setSelectedBatchId(null);
    setError("");
    setView("overview");
  };

  const requestTrace = async (targetUrl: string) => {
    const response = await fetch("/api/redirect-chain-checker", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ url: targetUrl, method, maxRedirects, profile, timeoutMs }),
    });
    const data = await response.json().catch(() => null);

    if (!response.ok) {
      throw new Error(data?.error ?? "Redirect chain could not be checked.");
    }

    return data as RedirectChainResponse;
  };

  const checkSingleRedirect = async () => {
    if (!url.trim()) {
      setError("Enter a URL first.");
      return;
    }

    setBusy(true);
    setError("");
    setResult(null);
    setBatchItems([]);
    setSelectedBatchId(null);

    try {
      const data = await requestTrace(url);

      setResult(data);
      setView("overview");
      rememberResults([data]);
    } catch (checkError) {
      setError(
        checkError instanceof Error ? checkError.message : "Redirect chain could not be checked.",
      );
    } finally {
      setBusy(false);
    }
  };

  const checkBatchRedirects = async () => {
    const urls = parseBatchUrls(batchUrls);

    if (!urls.length) {
      setError("Enter at least one URL.");
      return;
    }

    if (urls.length > MAX_BATCH_URLS) {
      setError(`Batch mode accepts up to ${MAX_BATCH_URLS} URLs at once.`);
      return;
    }

    const initialItems = urls.map((targetUrl, index) => ({
      id: `${index}-${targetUrl}`,
      url: targetUrl,
      status: "pending" as const,
    }));
    const completed: RedirectChainResponse[] = [];

    setBusy(true);
    setError("");
    setResult(null);
    setBatchItems(initialItems);
    setSelectedBatchId(null);
    setView("overview");

    for (const item of initialItems) {
      try {
        const data = await requestTrace(item.url);

        completed.push(data);
        setBatchItems((current) =>
          current.map((candidate) =>
            candidate.id === item.id ? { ...candidate, status: "complete", result: data } : candidate,
          ),
        );
        setSelectedBatchId((current) => current ?? item.id);
      } catch (batchError) {
        setBatchItems((current) =>
          current.map((candidate) =>
            candidate.id === item.id
              ? {
                  ...candidate,
                  status: "error",
                  error:
                    batchError instanceof Error
                      ? batchError.message
                      : "Redirect chain could not be checked.",
                }
              : candidate,
          ),
        );
      }
    }

    if (completed.length) {
      rememberResults(completed);
    }

    setBusy(false);
  };

  const checkRedirects = () => {
    if (inputMode === "batch") {
      void checkBatchRedirects();
      return;
    }

    void checkSingleRedirect();
  };

  const useExample = () => {
    if (inputMode === "batch") {
      setBatchUrls(["http://github.com", "http://example.com", "https://httpbin.org/redirect/2"].join("\n"));
      return;
    }

    setUrl("http://github.com");
  };

  return (
    <InputOutputPanel
      inputTitle="Redirect tracker"
      outputTitle={batchItems.length ? "Tracking results" : "Redirect trace"}
      inputActions={
        <>
          <Button type="button" variant="secondary" size="sm" onClick={useExample}>
            <Sparkles className="h-4 w-4" />
            Example
          </Button>
          <ClearButton onClear={clear} />
        </>
      }
      outputActions={
        <>
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-1">
            {(["overview", "timeline", "insights", "headers"] as const).map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={view === option ? "default" : "ghost"}
                onClick={() => setView(option)}
              >
                {option === "overview"
                  ? "Overview"
                  : option === "timeline"
                    ? "Timeline"
                    : option === "insights"
                      ? "Insights"
                      : "Headers"}
              </Button>
            ))}
          </div>
          <CopyButton value={reportValue} label="Copy report" />
          <CopyButton value={csvValue} label="Copy CSV" />
          <CopyButton value={jsonValue} label="Copy JSON" />
        </>
      }
      input={
        <div className="space-y-4">
          <div className="inline-flex rounded-md border border-zinc-200 bg-white p-1">
            {(["single", "batch"] as const).map((option) => (
              <Button
                key={option}
                type="button"
                size="sm"
                variant={inputMode === option ? "default" : "ghost"}
                onClick={() => setInputMode(option)}
              >
                {option === "single" ? "Single" : "Batch"}
              </Button>
            ))}
          </div>

          {inputMode === "single" ? (
            <div className="space-y-2">
              <Label htmlFor="redirect-url">URL</Label>
              <Input
                id="redirect-url"
                value={url}
                onChange={(event) => setUrl(event.target.value)}
                placeholder="https://example.com"
              />
            </div>
          ) : (
            <div className="space-y-2">
              <Label htmlFor="redirect-batch-urls">URLs</Label>
              <Textarea
                id="redirect-batch-urls"
                value={batchUrls}
                onChange={(event) => setBatchUrls(event.target.value)}
                placeholder={"https://example.com\nhttp://github.com"}
                className="min-h-48"
              />
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="redirect-method">Method</Label>
              <Select
                id="redirect-method"
                value={method}
                onChange={(event) => setMethod(event.target.value as "HEAD" | "GET")}
              >
                <option value="HEAD">HEAD</option>
                <option value="GET">GET with byte range</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="redirect-profile">Request profile</Label>
              <Select
                id="redirect-profile"
                value={profile}
                onChange={(event) => setProfile(event.target.value as RedirectRequestProfile)}
              >
                <option value="standard">Standard</option>
                <option value="browser">Browser-like</option>
                <option value="crawler">Crawler-style</option>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="redirect-max">Max redirects</Label>
              <Input
                id="redirect-max"
                type="number"
                min={0}
                max={20}
                value={maxRedirects}
                onChange={(event) => setMaxRedirects(Number(event.target.value))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="redirect-timeout">Timeout</Label>
              <Select
                id="redirect-timeout"
                value={String(timeoutMs)}
                onChange={(event) => setTimeoutMs(Number(event.target.value))}
              >
                <option value="5000">5 seconds</option>
                <option value="10000">10 seconds</option>
                <option value="15000">15 seconds</option>
              </Select>
            </div>
          </div>

          <ErrorMessage message={error} />
          <Button type="button" onClick={checkRedirects} disabled={busy}>
            {busy ? <Network className="h-4 w-4" /> : <Link2 className="h-4 w-4" />}
            {busy
              ? inputMode === "batch"
                ? "Tracking batch..."
                : "Tracking..."
              : inputMode === "batch"
                ? "Track batch"
                : "Track redirect"}
          </Button>

          {activeResult?.analysis.topFixes.length ? (
            <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
              <AlertCircle className="mt-0.5 h-4 w-4 flex-none" />
              <p>Review the priority cleanup items before changing canonical or migration rules.</p>
            </div>
          ) : null}

          <RecentRedirectHistory
            history={history}
            onPick={(pickedUrl) => {
              setInputMode("single");
              setUrl(pickedUrl);
              setResult(null);
              setBatchItems([]);
              setSelectedBatchId(null);
            }}
            onClear={() => {
              setHistory([]);
              writeRedirectHistory([]);
            }}
          />
        </div>
      }
      output={
        <RedirectTrackerOutput
          batchItems={batchItems}
          selectedBatchId={selectedBatchId}
          onSelectBatch={setSelectedBatchId}
          result={result}
          view={view}
        />
      }
    />
  );
}
